import { useCallback, useEffect, useMemo, useState } from 'react';
import { Surface, cardStyle, eyebrowStyle, pageStyle } from '@kilat/ui';
import { Link } from 'react-router-dom';
import {
  brandCapabilities,
  computeIntegrationReadiness,
  computeSupplierLegalSteps,
  type BuildTimeKeyInput,
  type LaunchReadiness,
  type PlatformBrand,
  type ReadinessState,
} from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import {
  PAGE_FRAME_MAX,
  cardGridStyle,
  dashboardPageStyle,
  sectionGridStyle,
} from '../lib/layout.js';
import { env } from '../lib/env.js';
import { callListIntegrations } from '../lib/callables/integrations.js';
import { platformBrandsRepo } from '../lib/repositories/platform-brands-repo.js';
import { moneyPolicyRepo } from '../lib/repositories/platform-config-repo.js';

/**
 * /superadmin/setup — Platform Setup Wizard / launch-readiness guide.
 *
 * Reads the same integration data as the Integrations panel and tells the owner
 * exactly what must be configured before the platform is ready to launch:
 * required steps (e.g. toyyibPay keys), recommended ones, and optional extras —
 * with a clear "ready / not ready" verdict. The verdict comes from the pure
 * `computeIntegrationReadiness` model so the wizard can never disagree with it.
 */

/**
 * The build-time client keys the wizard checks. `isSet` reflects THIS dashboard
 * build; each app is compiled separately, which the readiness model states in
 * the step copy rather than letting a green tick imply otherwise.
 */
const BUILD_TIME_KEYS: readonly BuildTimeKeyInput[] = [
  {
    name: 'VITE_RECAPTCHA_SITE_KEY',
    label: 'App Check site key (reCAPTCHA Enterprise)',
    requirement: 'required',
    isSet: (env.recaptchaSiteKey ?? '').trim().length > 0,
    consequence: 'Every App-Check-enforcing callable fails in production.',
    consumedBy: ['dashboard', 'customer-pwa', 'agent-pwa', 'marketing'],
  },
  {
    name: 'VITE_FCM_VAPID_KEY',
    label: 'Web Push key (FCM VAPID)',
    requirement: 'required',
    isSet: (env.fcmVapidKey ?? '').trim().length > 0,
    consequence: 'Push notifications silently never arrive.',
    consumedBy: ['customer-pwa'],
  },
];

export function SuperadminSetupPage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  const [readiness, setReadiness] = useState<LaunchReadiness | null>(null);
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'ready' } | { kind: 'error'; message: string }
  >({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [res, policy] = await Promise.all([
        callListIntegrations(brand.id),
        moneyPolicyRepo.get(),
      ]);
      const r = computeIntegrationReadiness(
        res.items.map((it) => ({
          service: it.service,
          label: it.label,
          launchRequirement: it.launchRequirement,
          status: it.status,
          runbook: it.runbook,
          secrets: it.secrets.map((s) => ({ name: s.name, required: s.required, isSet: s.isSet })),
        })),
        // G3 — build-time client keys. These are NOT panel-configurable and
        // need a rebuild, so they never appeared in the integrations list and
        // the wizard reported "ready to launch" while App Check would have
        // rejected every callable in production and push was silently dead.
        BUILD_TIME_KEYS,
        // The legal entity printed as "FROM" on every operator invoice. It
        // shipped as TODO(owner) blanks in the seed and cannot be guessed.
        computeSupplierLegalSteps({
          ...brand.legal,
          sstRegistered: policy?.supplierTax.sstRegistered ?? false,
        }),
      );
      setReadiness(r);
      setState({ kind: 'ready' });
    } catch (err) {
      logger.error('setup: load failed', { error: String(err) });
      setState({ kind: 'error', message: friendly(err) });
    }
  }, [brand.id, brand.legal]);

  useEffect(() => {
    if (isSuper) void load();
  }, [isSuper, load]);

  if (authLoading) return <Screen title="Loading…" />;
  if (!isSuper) {
    return (
      <Screen
        title="Super-admin only"
        body={`Setup is restricted to ${brand.name} super-admins.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <header style={headerStyle}>
        <div style={{ maxWidth: PAGE_FRAME_MAX, margin: '0 auto', padding: 'var(--space-5)' }}>
          <p style={eyebrowStyle}>Super-admin · Setup</p>
          <h1
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}
          >
            Platform setup
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              maxWidth: 640,
            }}
          >
            What needs configuring before {brand.name} is ready to use. Everything is set from the{' '}
            <Link to="/superadmin/integrations" style={linkStyle}>
              Integrations panel
            </Link>{' '}
            — no config files.
          </p>
        </div>
      </header>

      <section style={contentStyle}>
        {state.kind === 'loading' ? <Card>Checking what’s configured…</Card> : null}
        {state.kind === 'error' ? (
          <Card>
            <p style={{ margin: 0, fontWeight: 600 }}>Couldn’t load setup status</p>
            <p
              style={{
                margin: 'var(--space-2) 0 var(--space-3) 0',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {state.message}
            </p>
            <button style={btnStyle} onClick={() => void load()}>
              Try again
            </button>
          </Card>
        ) : null}

        {state.kind === 'ready' && readiness ? <Readiness r={readiness} /> : null}

        <LaunchCapabilities brand={brand} />
      </section>
    </main>
  );
}

type CapabilityKey = 'recruiterChannel' | 'marketplaceWashPayments';

/**
 * ADR-0023 — panel-configurable launch capability flags. Two reversible
 * switches persisted onto the platform brand doc via the existing
 * `platformBrandsRepo.update` write path (the same repo `usePlatformBrand`
 * live-reads), so a flip propagates back to this card and to every app without
 * a reload. Defaults are behaviour-neutral (both on).
 */
function LaunchCapabilities({ brand }: { readonly brand: PlatformBrand }) {
  const caps = brandCapabilities(brand);
  const [saving, setSaving] = useState<CapabilityKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setFlag = useCallback(
    async (key: CapabilityKey, next: boolean) => {
      setSaving(key);
      setError(null);
      try {
        await platformBrandsRepo.update(brand.id, { [`capabilities.${key}`]: next });
      } catch (err) {
        logger.error('setup: capability toggle failed', { key, error: String(err) });
        setError(friendly(err));
      } finally {
        setSaving(null);
      }
    },
    [brand.id],
  );

  return (
    <div style={{ marginTop: 'var(--space-6)' }}>
      <p style={subheadStyle}>Launch capabilities</p>
      <Surface as="div">
        <p
          style={{
            margin: 0,
            color: 'var(--color-on-surface-muted)',
            fontSize: 'var(--text-meta)',
            lineHeight: 1.5,
          }}
        >
          Reversible switches (ADR-0023) that narrow what {brand.name} launches with. Saved to the
          brand instantly; each app picks up the change on next load. Turning one off never deletes
          the underlying data.
        </p>
        {/* The two switches side by side on a wide screen. */}
        <div style={{ ...sectionGridStyle, rowGap: 0 }}>
          <ToggleRow
            title="Recruiter channel"
            detail="The sales-agent recruiter layer — recruiter sign-ups, the agent-pwa recruiter dashboard, and recruiter commissions. Off = defer it for launch; existing recruiter records stay intact."
            on={caps.recruiterChannel}
            busy={saving === 'recruiterChannel'}
            disabled={saving !== null}
            onToggle={(next) => void setFlag('recruiterChannel', next)}
          />
          <ToggleRow
            title="Marketplace wash payments"
            detail="Kilat-collected online wash payments (merchant-of-record via toyyibPay). Live today — turning it off drops wash collection to cash / the operator's own QR."
            on={caps.marketplaceWashPayments}
            busy={saving === 'marketplaceWashPayments'}
            disabled={saving !== null}
            onToggle={(next) => void setFlag('marketplaceWashPayments', next)}
          />
        </div>
        {error ? (
          <p role="alert" style={{ margin: 0, color: '#b42318', fontSize: 'var(--text-meta)' }}>
            {error} The switch stayed as it was — try again.
          </p>
        ) : null}
      </Surface>
    </div>
  );
}

function ToggleRow({
  title,
  detail,
  on,
  busy,
  disabled,
  onToggle,
}: {
  readonly title: string;
  readonly detail: string;
  readonly on: boolean;
  readonly busy: boolean;
  readonly disabled: boolean;
  readonly onToggle: (next: boolean) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
        padding: 'var(--space-4) 0 0 0',
        marginTop: 'var(--space-4)',
        borderTop: '1px solid var(--color-border)',
      }}
    >
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>{title}</p>
        <p
          style={{
            margin: '4px 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {detail}
        </p>
      </div>
      <div style={{ display: 'grid', gap: 4, justifyItems: 'end', flexShrink: 0 }}>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={title}
          disabled={disabled}
          onClick={() => onToggle(!on)}
          style={{
            width: 48,
            height: 28,
            borderRadius: 999,
            border: 'none',
            padding: 3,
            cursor: disabled ? 'wait' : 'pointer',
            background: on ? 'var(--color-primary)' : 'rgba(15,23,42,0.22)',
            display: 'inline-flex',
            justifyContent: on ? 'flex-end' : 'flex-start',
            alignItems: 'center',
            opacity: disabled && !busy ? 0.5 : 1,
            transition: 'background 160ms ease',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 22,
              height: 22,
              borderRadius: 999,
              background: '#ffffff',
              boxShadow: '0 1px 2px rgba(15,23,42,0.3)',
            }}
          />
        </button>
        <span style={{ fontSize: 11, color: 'var(--color-on-surface-muted)' }}>
          {busy ? 'Saving…' : on ? 'On' : 'Off'}
        </span>
      </div>
    </div>
  );
}

function Readiness({ r }: { readonly r: LaunchReadiness }) {
  const groups: { title: string; req: 'required' | 'recommended' | 'optional' }[] = [
    { title: 'Required to launch', req: 'required' },
    { title: 'Recommended', req: 'recommended' },
    { title: 'Optional', req: 'optional' },
  ];
  return (
    <>
      {/* Verdict banner */}
      <div
        style={{
          ...cardStyle,
          marginBottom: 'var(--space-5)',
          borderColor: r.ready ? 'rgba(13,122,79,0.32)' : 'rgba(245,158,11,0.4)',
          background: r.ready ? 'rgba(13,122,79,0.06)' : 'rgba(245,158,11,0.07)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-h2, 22px)', fontWeight: 700 }}>
          {r.ready
            ? '✓ Ready to launch'
            : `⚠ ${r.requiredTotal - r.requiredDone} required step(s) left`}
        </h2>
        <p style={{ margin: 'var(--space-2) 0 0 0', color: 'var(--color-on-surface-muted)' }}>
          {r.requiredDone} of {r.requiredTotal} required steps done.{' '}
          {r.ready
            ? 'All must-have integrations are configured. Recommended/optional items below are up to you.'
            : 'Finish the required steps below and the platform is good to go.'}
        </p>
        <Link
          to="/superadmin/integrations"
          style={{
            ...btnStyle,
            display: 'inline-block',
            marginTop: 'var(--space-4)',
            textDecoration: 'none',
          }}
        >
          Configure integrations →
        </Link>
      </div>

      {groups.map((g) => {
        const steps = r.steps.filter((s) => s.requirement === g.req);
        if (steps.length === 0) return null;
        return (
          <div key={g.req} style={{ marginBottom: 'var(--space-5)' }}>
            <p style={subheadStyle}>{g.title}</p>
            {/* Steps as a grid of cards: a wide screen reads a whole group at a
                glance instead of scrolling one narrow column. */}
            <div style={{ ...cardGridStyle, gap: 'var(--space-3)' }}>
              {steps.map((s) => (
                <div key={s.service} style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{ICON[s.state]}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>{s.label}</p>
                      <p
                        style={{
                          margin: '4px 0 0 0',
                          fontSize: 'var(--text-meta)',
                          color: TONE[s.state],
                        }}
                      >
                        {s.detail}
                      </p>
                    </div>
                    <Link to="/superadmin/integrations" style={linkStyle}>
                      Set up →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

const ICON: Record<ReadinessState, string> = {
  done: '✅',
  action_needed: '⚠️',
  blocked: '🚫',
  optional: '⭕',
};
const TONE: Record<ReadinessState, string> = {
  done: 'var(--color-primary)',
  action_needed: '#b45309',
  blocked: '#b42318',
  optional: 'var(--color-on-surface-muted)',
};

// ── shared bits ─────────────────────────────────────────────────────────────

function Card({ children }: { readonly children: React.ReactNode }) {
  return <Surface as="div">{children}</Surface>;
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body?: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main style={{ ...pageStyle, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 'var(--space-6)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h1>
        {body ? (
          <p style={{ margin: 'var(--space-3) 0 0 0', color: 'var(--color-on-surface-muted)' }}>
            {body}
          </p>
        ) : null}
        {cta ? (
          <Link
            to={cta.to}
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-4)',
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

function friendly(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('permission-denied')) return 'You’re not a super-admin for this brand.';
  if (msg.includes('unauthenticated')) return 'Your session expired — sign in again.';
  return 'Something went wrong. Try again.';
}
const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
};
const contentStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5) var(--space-5) var(--space-12) var(--space-5)',
};
const subheadStyle: React.CSSProperties = {
  margin: '0 0 var(--space-2) 0',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};
const btnStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  border: 'none',
  fontWeight: 500,
  cursor: 'pointer',
};
const linkStyle: React.CSSProperties = {
  color: 'var(--color-primary)',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};
